import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Eye, EyeOff, ArrowLeft, Music } from "lucide-react";
import { GenrePicker } from "@/components/GenrePicker";

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get("tab") !== "register");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [form, setForm] = useState({
    email: "", password: "", pseudo: "", role: "fan",
    artist_name: "", city: "", genres: [], age_confirmed: false,
  });

  const toggleGenre = (g) => {
    setForm((f) => ({
      ...f,
      genres: f.genres.includes(g)
        ? f.genres.filter((x) => x !== g)
        : [...f.genres, g],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        const u = await login(form.email, form.password);
        if (!u.email_verified && u.role !== "admin") {
          navigate("/verify-email");
        } else {
          navigate(u.role === "admin" ? "/admin" : u.role === "artist" ? "/dashboard" : "/discover");
        }
      } else {
        await register(form);
        navigate("/verify-email");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center px-4 py-8" data-testid="auth-page">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#FF6B35] opacity-[0.06] blur-[120px]" />

      <div className="w-full max-w-md relative z-10">
        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#AFA8BA] mb-8 transition-colors"
          data-testid="auth-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading font-extrabold text-4xl text-gradient mb-2">ReallyFan</h1>
          <p className="text-sm text-muted-foreground">
            {isLogin ? "Content de te revoir" : "Rejoins la communaute"}
          </p>
        </div>

        {/* Toggle */}
        <div className="flex bg-foreground/5 rounded-full p-1 mb-8 border border-foreground/5">
          <button
            onClick={() => { setIsLogin(true); setError(""); }}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
              isLogin ? "bg-[#FF6B35] text-white shadow-lg" : "text-muted-foreground"
            }`}
            data-testid="auth-login-tab"
          >
            Connexion
          </button>
          <button
            onClick={() => { setIsLogin(false); setError(""); }}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
              !isLogin ? "bg-[#FF6B35] text-white shadow-lg" : "text-muted-foreground"
            }`}
            data-testid="auth-register-tab"
          >
            Inscription
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-2xl px-4 py-3 text-sm text-red-400" data-testid="auth-error">
              {error}
            </div>
          )}

          <Input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className="bg-foreground/5 border-foreground/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-12"
            data-testid="auth-email-input"
          />

          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Mot de passe"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              className="bg-foreground/5 border-foreground/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-12 pr-10"
              data-testid="auth-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {!isLogin && (
            <>
              <Input
                placeholder="Pseudo"
                value={form.pseudo}
                onChange={(e) => setForm({ ...form, pseudo: e.target.value })}
                required
                className="bg-foreground/5 border-foreground/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-12"
                data-testid="auth-pseudo-input"
              />

              {/* Role selector */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, role: "fan" })}
                  className={`flex-1 py-3 rounded-2xl text-sm font-medium border transition-all duration-200 ${
                    form.role === "fan"
                      ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                      : "bg-foreground/5 border-foreground/10 text-muted-foreground"
                  }`}
                  data-testid="auth-role-fan"
                >
                  Fan / Public
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, role: "artist" })}
                  className={`flex-1 py-3 rounded-2xl text-sm font-medium border transition-all duration-200 flex items-center justify-center gap-2 ${
                    form.role === "artist"
                      ? "bg-[#7000FF]/10 border-[#7000FF]/40 text-[#7000FF]"
                      : "bg-foreground/5 border-foreground/10 text-muted-foreground"
                  }`}
                  data-testid="auth-role-artist"
                >
                  <Music className="w-4 h-4" />
                  Artiste
                </button>
              </div>

              {form.role === "artist" && (
                <Input
                  placeholder="Nom d'artiste"
                  value={form.artist_name}
                  onChange={(e) => setForm({ ...form, artist_name: e.target.value })}
                  required
                  className="bg-foreground/5 border-foreground/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-12"
                  data-testid="auth-artist-name-input"
                />
              )}

              <Input
                placeholder="Ville (optionnel)"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="bg-foreground/5 border-foreground/10 rounded-xl text-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-12"
                data-testid="auth-city-input"
              />

              {/* Genre chips */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Genres musicaux</p>
                <GenrePicker
                  selected={form.genres}
                  onChange={(genres) => setForm({ ...form, genres })}
                  dark={true}
                />
              </div>

              {/* Age verification */}
              <label className="flex items-start gap-3 cursor-pointer" data-testid="age-confirm-label">
                <input
                  type="checkbox"
                  checked={form.age_confirmed}
                  onChange={(e) => setForm({ ...form, age_confirmed: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-white/20 bg-foreground/5 text-[#FF6B35] focus:ring-[#FF6B35] shrink-0"
                  data-testid="age-confirm-checkbox"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  Je confirme avoir au moins <strong className="text-[#AFA8BA]">15 ans</strong> et j'accepte les{" "}
                  <a href="/legal" target="_blank" className="text-[#FF6B35] hover:underline">CGU</a> et la{" "}
                  <a href="/privacy" target="_blank" className="text-[#FF6B35] hover:underline">Politique de confidentialite</a>
                </span>
              </label>
            </>
          )}

          <button
            type="submit"
            disabled={loading || (!isLogin && !form.age_confirmed)}
            className="w-full bg-[#FF6B35] text-white font-bold py-3.5 rounded-full shadow-[0_0_20px_rgba(255,107,53,0.4)] hover:shadow-[0_0_40px_rgba(255,107,53,0.6)] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? "..." : isLogin ? "Se connecter" : "Creer mon compte"}
          </button>

          {!isLogin && (
            <p className="text-center text-xs text-muted-foreground mt-4">
              100 credits offerts a l'inscription
            </p>
          )}
        </form>
      </div>

      <div className="noise-overlay" />
    </div>
  );
}
